<template>
  <div>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-data-table
      v-bind="dataTableOptions"
      :items="items"
      hide-default-header
      item-key="id"
      class="elevation-0 content"
      :loading="loading"
    >
      <template #header="{ props }">
        <thead class="v-data-table-header">
          <tr>
            <th v-for="(header, index) in props.headers" :key="index" :class="classes.header.th">
              <v-tooltip v-if="headerEllipsis" top>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" :class="classes.header.text">{{ header.text }}</div>
                </template>
                <span class="tooltip">{{ header.text }}</span>
              </v-tooltip>
              <span v-else class="singleline">{{ header.text }}</span>
            </th>
          </tr>
        </thead>
      </template>
      <template #item="{ item, headers }">
        <tr :class="{ 'v-data-table__selected': item.isSelected }" @click="onClickRow($event, item)">
          <td v-for="(header, index) in headers" :key="index" :class="getItemTdClasses(header.align)" :style="getCellStyle(header, item)">
            <!--
              itemEllipsis 가 설정되면 생략된 전체 컬럼 데이터가 tooltip으로 보여지게 display.
             -->
            <v-tooltip v-if="itemEllipsis" top>
              <template #activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" :class="this.classes.item.text">{{ converter(item[header.value], header.converter) }}</div>
              </template>
              <span class="tooltip">{{ item[header.value] }}</span>
            </v-tooltip>
            <!--
              itemEllipsis 가 설정안되고,
              settingsGrid에서 type이 설정되었고 type=icon 이면 value.icon과 value.style을 이용해서 아이콘을 표시하고
              value.msgCode를 이용하여 i18n 메시지가 tooltip으로 보여지게 display.
             -->
            <v-tooltip v-else-if="!itemEllipsis && header.type && header.type == 'icon'" top>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" :style="item[header.value].style == 'theme' ? getThemeColor() : item[header.value].style" small>{{ item[header.value].icon }}</v-icon>
              </template>
              <span class="tooltip">{{ $t(`${item[header.value].msgCode}`) }}</span>
            </v-tooltip>
            <!--
              itemEllipsis 가 설정안되고,
              settingsGrid에서 type이 Array로 설정되었고 type[0]=enumtype 이면 
              value[type[1]] 데이터가 필드에 표시되게 display.
             -->
            <span v-else-if="!itemEllipsis && header.type && Array.isArray(header.type) && header.type[0] == 'enumtype'" class="singleline">{{ converter(item[header.value][header.type[1]], header.converter) }}</span>
            <!--
              그 외의 경우에는 평범하게 value 값이 보여지게 display.
             -->
            <span v-else class="singleline">{{ converter(item[header.value], header.converter) }}</span>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { merge } from 'lodash';

export default {
  // VDataTable options
  props: {
    headers: Array,
    items: Array,
    height: [Number, String],
    fixedHeader: Boolean,
    dense: Boolean,
    disablePagination: Boolean,
    hideDefaultFooter: Boolean,
    singleSelect: Boolean,
    clickRow: Function,
    loading: Boolean,

    // SingleLineHeaderDataTable options
    headerEllipsis: {
      type: Boolean,
      default: true,
    },
    // Comment.by.KWShin.2023.07.26.
    // itemEllipsis 옵션과
    // settingsGrid에서의 type 설정을 동시에 사용하지 못함.
    // v-tooltip 기능이 겹쳐서 동시에 지원할 수 없음.
    itemEllipsis: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      classes: {
        header: {
          th: {
            'content-max-width': this.headerEllipsis,
          },
          text: {
            ellipsis: this.headerEllipsis,
          },
        },
        item: {
          td: {
            'content-max-width': this.itemEllipsis,
          },
          text: {
            ellipsis: this.itemEllipsis,
          },
        },
      },
    };
  },
  methods: {
    // BEGIN::Added.by.KWShin.
    getThemeColor() {
      return this.$vuetify.theme.isDark ? 'color:white;' : 'color:black;';
    },
    /**
     * settingsGrid의 cellColor(예: 'instColor', 'wpTypeColor')에 해당하는
     * row 필드 값을 글자색으로 사용한다.
     */
    getCellStyle(header, item) {
      if (!header.cellColor || !item[header.cellColor]) return {};
      return { color: item[header.cellColor] };
    },
    // END::Added.by.KWShin.
    getItemTdClasses(align) {
      const textAlign = `text-${align || 'start'}`;
      return merge({}, this.classes.item.td, { [textAlign]: true });
    },
    converter(value, name) {
      const converter = {
        name,
        argument: undefined,
      };
      if (Array.isArray(name)) {
        converter.name = name[0];
        converter.argument = name[1];
      }
      return converter.name ? (this.$options.filters[converter.name] ? this.$options.filters[converter.name](value, converter.argument) : value) : value;
    },
    onClickRow(event, item) {
      const vm = this;
      this.$emit('click:row', item, {
        headers: this.headers,
        isSelected: item.isSelected,
        select(value) {
          vm.$set(item, 'isSelected', value);
          if (value) {
            const selectedIndex = vm.items.indexOf(item);
            vm.items.forEach((n, index) => {
              if (index !== selectedIndex) {
                if (n.isSelected) {
                  n.isSelected = false;
                  return false;
                }
              }
            });
          }
        },
      });
    },
  },
  computed: {
    dataTableOptions() {
      const options = merge({}, this.$props);
      delete options.items;
      return options;
    },
  },
};
</script>

<style scoped>
.content-max-width {
  max-width: 150px;
}
.singleline {
  white-space: nowrap !important;
}
.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tooltip {
  font-size: 11px;
}
</style>
